import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Sparkles, CheckCircle2, Clock, ShieldCheck, Phone, MessageCircle,
  Building2, User, Send, Loader2, Crown, ArrowRight, Plus, Minus,
  MapPin, BadgeCheck, HelpCircle, Lightbulb, Heart, DollarSign, AlertTriangle, X
} from 'lucide-react';
import { API } from '../utils/api';
import Reto21TestimoniosCarousel from '../components/Reto21TestimoniosCarousel';
import { CHILEAN_REGIONS } from '../constants/chileanLocations';

var CENTER_MODALITIES = [
  { value: 'residencial', label: 'Residencial', desc: 'Internación 24/7' },
  { value: 'ambulatorio', label: 'Ambulatorio', desc: 'Sesiones regulares' },
  { value: 'online', label: 'Online', desc: 'Telemedicina' },
  { value: 'gratis', label: 'Gratis (SENDA/FONASA)', desc: 'Sujeto a lista de espera 1–5 meses', warn: true }
];
var PRO_TYPES = [
  { value: 'psicologo', label: 'Psicólogo/a' },
  { value: 'psiquiatra', label: 'Psiquiatra' },
  { value: 'terapeuta', label: 'Terapeuta' },
  { value: 'consejero', label: 'Consejero/a' },
  { value: 'gratis', label: 'Gratis (SENDA/FONASA)', desc: 'Sujeto a lista de espera 1–5 meses', warn: true }
];
var ADDICTIONS = ['Alcohol', 'Cocaína', 'Pasta Base', 'Marihuana', 'Benzodiacepinas / Medicamentos', 'Ludopatía', 'Adicciones conductuales', 'Patología dual', 'Otras'];
var URGENCY = [
  { value: 'inmediata', label: 'Inmediata' },
  { value: 'esta_semana', label: 'Esta semana' },
  { value: 'flexible', label: 'Flexible' }
];

// Rangos de presupuesto por modalidad (CLP mensual)
var BUDGET_RANGES = {
  residencial: [
    { value: '350-450', label: '$350.000 – $450.000', hint: 'Programa básico, capacidad estándar' },
    { value: '450-750', label: '$450.000 – $750.000', hint: 'Programa intermedio, más profesionales' },
    { value: '750-1000', label: '$750.000 – $1.000.000', hint: 'Alto estándar, atención personalizada' },
    { value: '1000+', label: '+$1.000.000', hint: 'Premium: infraestructura, staff y beneficios superiores' }
  ],
  ambulatorio: [
    { value: '250-450', label: '$250.000 – $450.000', hint: 'Sesiones básicas semanales' },
    { value: '450-650', label: '$450.000 – $650.000', hint: 'Intermedio: más sesiones y grupos' },
    { value: '700+', label: '+$700.000', hint: 'Alto estándar: mayor frecuencia y equipo multidisciplinario' }
  ],
  online: [
    { value: '150-250', label: '$150.000 – $250.000', hint: 'Programa básico de acompañamiento' },
    { value: '250-350', label: '$250.000 – $350.000', hint: 'Sesiones regulares + herramientas' },
    { value: '400+', label: '+$400.000', hint: 'Mayor frecuencia, seguimiento intensivo' }
  ],
  // Profesionales
  psicologo: [
    { value: 'consulta', label: 'Consulta única ($30.000 – $50.000)', hint: 'Primera sesión / evaluación' },
    { value: 'paquete', label: 'Paquete 4 sesiones ($100.000 – $200.000)', hint: 'Programa corto' },
    { value: 'mensual', label: 'Programa mensual (+$200.000)', hint: 'Acompañamiento continuo' }
  ]
};
BUDGET_RANGES.psiquiatra = BUDGET_RANGES.psicologo;
BUDGET_RANGES.terapeuta = BUDGET_RANGES.psicologo;
BUDGET_RANGES.consejero = BUDGET_RANGES.psicologo;

var FAQS = [
  {
    q: '¿Cuánto cuesta un centro de rehabilitación en Chile?',
    a: 'Depende del tipo: un programa residencial (internación) va desde $800.000 hasta $4.000.000 mensuales. Los ambulatorios suelen estar entre $150.000 y $500.000 mensuales. Los online son los más accesibles: desde $80.000 a $250.000. Al cotizar con varios centros recibes los precios reales y comparables.'
  },
  {
    q: '¿Cuál es la diferencia entre modalidades?',
    a: 'Residencial: el paciente vive en el centro 24/7 durante semanas o meses. Ideal para casos severos o pasta base. Ambulatorio: el paciente asiste 2-5 veces por semana y vive en su casa. Bueno para etapas de mantención o casos leves. Online: sesiones por videollamada con psiquiatra/psicólogo. Útil para seguimiento o zonas rurales.'
  },
  {
    q: '¿Por qué cotizar con varios centros a la vez?',
    a: 'Cada centro tiene enfoque, equipo y precio diferente. Al comparar 5-10 opciones evitas elegir el primero que te contactó. En salud mental la química con el equipo es clave — tener opciones te da poder de decisión en un momento difícil.'
  },
  {
    q: '¿Mi información queda pública o se comparte con terceros?',
    a: 'No. Tus datos solo van a los centros matching de tu región y modalidad. No aparecen en directorios públicos, no se venden a marketing, y puedes borrarte cuando quieras. Confidencialidad 100% garantizada.'
  },
  {
    q: '¿Qué pasa si el centro que elijo no tiene cupo?',
    a: 'Al cotizar con varios, tienes backups inmediatos. Los centros Elite suelen confirmar disponibilidad en el primer mensaje. Si un centro no tiene cupo, los otros 10-20 matching sí probablemente sí — por eso distribuimos a todos.'
  },
  {
    q: '¿FONASA o ISAPRE cubren tratamiento de adicciones?',
    a: 'FONASA cubre parcialmente internaciones vía bonificación. Algunas ISAPRES cubren tratamiento ambulatorio psiquiátrico con copago. Al cotizar, pregunta específicamente por cobertura: muchos centros tienen convenios con aseguradoras y descuentos.'
  },
  {
    q: '¿Cuánto tiempo demora el tratamiento?',
    a: 'Residencial típico: 3-6 meses + reinserción ambulatoria de 6-12 meses. Ambulatorio: 6-24 meses. Online: indefinido (según necesidad). Lo importante no es el "tiempo" sino la adherencia. Tratamientos más cortos con abandono son peores que uno largo con compromiso.'
  }
];

var WHAT_TO_ASK = [
  { title: 'Enfoque terapéutico', desc: 'Pregunta si usan modelo Minnesota, cognitivo-conductual, comunidad terapéutica, dual diagnosis, etc. No es lo mismo un enfoque u otro según el caso.' },
  { title: 'Equipo profesional', desc: '¿Hay psiquiatra disponible 24/7? ¿Cuántos psicólogos por paciente? ¿Tienen médico general? La calidad del equipo marca la diferencia.' },
  { title: 'Costo real y coberturas', desc: 'Pide un desglose: mensualidad, medicamentos aparte, exámenes iniciales, actividades extras. Consulta por descuentos FONASA/ISAPRE.' },
  { title: 'Instalaciones', desc: '¿Puedo visitar el centro? ¿Cómo son las habitaciones? ¿Hay áreas verdes, gym, talleres? El entorno físico afecta la recuperación.' },
  { title: 'Seguimiento post-alta', desc: '¿Qué pasa después del programa? Los centros serios ofrecen ambulatorio de seguimiento, grupos de ex pacientes, acompañamiento familiar.' },
  { title: 'Reglas de visita y comunicación', desc: '¿Puedo ver a mi familiar? ¿Con qué frecuencia? ¿Puede llamar por teléfono? Importante para decidir según las necesidades familiares.' }
];

export default function CotizacionLanding() {
  var [eliteCenters, setEliteCenters] = useState([]);
  var [step, setStep] = useState(1);
  var [loading, setLoading] = useState(false);
  var [done, setDone] = useState(null);
  var [matchCount, setMatchCount] = useState(null);
  var [openFaq, setOpenFaq] = useState(0);
  var [entrySource, setEntrySource] = useState('cotizar-landing');
  var [suggestedModality, setSuggestedModality] = useState('');
  var [showGratisModal, setShowGratisModal] = useState(false);
  var location = useLocation();
  var navigate = useNavigate();
  var [form, setForm] = useState({
    target_type: 'center', region: '', modality: '', addiction: '', urgency: '',
    budget_range: '', can_use_medical_license: false,
    name: '', phone: '', email: '', message: ''
  });

  // Leer query params para pre-rellenar desde experiencias (/yo-del-futuro, /experiencia)
  useEffect(function() {
    if (!location.search) return;
    var qs = new URLSearchParams(location.search);
    var src = qs.get('source');
    var sug = qs.get('modalidad_sugerida');
    var nombre = qs.get('nombre');
    if (src) setEntrySource(src);
    if (sug) {
      setSuggestedModality(sug);
      // Pre-seleccionar modalidad (ej: 'residencial' → buscar coincidencia en CENTER_MODALITIES)
      setForm(function(f) {
        var newForm = Object.assign({}, f);
        // intenta mapear valor sugerido al value real del modality select
        var normalized = sug.toLowerCase();
        newForm.modality = normalized;
        return newForm;
      });
    }
    if (nombre) {
      setForm(function(f) { return Object.assign({}, f, { name: nombre }); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function() {
    // Load Elite centers (plan_level=3) as social proof
    axios.get(API + '/centers/public?plan_level=3&limit=6')
      .then(function(r) { setEliteCenters(r.data.centers || []); })
      .catch(function() { setEliteCenters([]); });
  }, []);

  useEffect(function() {
    if (step < 2 || !form.region) { setMatchCount(null); return; }
    var params = new URLSearchParams();
    params.set('target_type', form.target_type);
    if (form.region) params.set('region', form.region);
    if (form.modality) params.set('modality', form.modality);
    axios.get(API + '/quote-requests/match-count?' + params.toString())
      .then(function(r) { setMatchCount(r.data.count || 0); })
      .catch(function() { setMatchCount(null); });
  }, [form.region, form.modality, form.target_type, step]);

  function update(k, v) { setForm(function(f) { return Object.assign({}, f, { [k]: v }); }); }

  async function submit() {
    setLoading(true);
    try {
      var res = await axios.post(API + '/quote-requests', Object.assign({}, form, {
        source: entrySource
      }));
      setDone(res.data);
      // GA4 dataLayer push → generate_lead (key conversion)
      try {
        const a = await import('../services/analytics');
        a.trackEvent('cotizacion_submitted', 'quote_request', form.target_type);
      } catch (e) { /* noop */ }
      if (typeof window !== 'undefined' && window.gtag) {
        try { window.gtag('event', 'quote_landing_submit', { event_category: 'lead', event_label: form.target_type, value: res.data.matched_count }); } catch(e) {}
      }
      var el = document.getElementById('form-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      setDone({ success: false, message: 'Error enviando la solicitud. Intenta nuevamente.' });
    }
    setLoading(false);
  }

  var modalities = form.target_type === 'center' ? CENTER_MODALITIES : PRO_TYPES;
  var canNext1 = form.target_type && form.region;
  var canNext2 = form.modality && form.modality !== 'gratis' && form.budget_range;
  var canSubmit = form.name.trim() && form.phone.trim();
  var budgetOptions = BUDGET_RANGES[form.modality] || [];

  function handleModalityClick(value) {
    if (value === 'gratis') {
      setShowGratisModal(true);
      return;
    }
    update('modality', value);
    // Reset budget si cambia modalidad
    update('budget_range', '');
  }

  function scrollToForm() {
    var el = document.getElementById('form-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>Cotiza tu Tratamiento de Rehabilitación en Chile | SinAdicciones.org</title>
        <meta name="description" content="Recibe cotizaciones de centros de rehabilitación y profesionales en Chile. Compara precios, modalidades y enfoques en 2 minutos. Servicio gratuito, confidencial y sin compromiso." />
        <link rel="canonical" href="https://sinadicciones.org/cotizar" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/cotizar" />
        <meta property="og:title" content="Cotiza tu Tratamiento de Rehabilitación Gratis" />
        <meta property="og:description" content="Compara hasta 20 centros de rehabilitación en tu región en 2 minutos. Cotización gratuita y confidencial." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cotiza tu Tratamiento de Rehabilitación Gratis" />
        <meta name="twitter:description" content="Compara hasta 20 centros en 2 minutos. Gratuito y confidencial." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      
      <Header />

      <main className="pt-20 pb-16" data-testid="cotizar-landing">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-blue-950/40 via-slate-950 to-slate-950 border-b border-slate-800">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-500/10 blur-3xl rounded-full"></div>
          </div>
          <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 relative">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-semibold mb-4">
                  <Sparkles className="w-3 h-3" /><span>Gratis · Confidencial · Sin compromiso</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-5">
                  Cotiza tu tratamiento <span className="text-emerald-400">en 2 minutos</span>
                </h1>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Recibe propuestas personalizadas de <strong className="text-white">hasta 20 centros y profesionales</strong> de tu región. Compara precios, modalidades y enfoques. Toma una decisión informada en uno de los momentos más importantes de tu vida.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-8">
                  <div className="text-center bg-slate-900/50 border border-slate-800 rounded-xl p-3">
                    <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400 mt-1">Respuesta en <strong className="text-slate-200 block">&lt; 24 h</strong></p>
                  </div>
                  <div className="text-center bg-slate-900/50 border border-slate-800 rounded-xl p-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400 mt-1">100% <strong className="text-slate-200 block">confidencial</strong></p>
                  </div>
                  <div className="text-center bg-slate-900/50 border border-slate-800 rounded-xl p-3">
                    <Heart className="w-5 h-5 text-rose-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400 mt-1">Sin <strong className="text-slate-200 block">compromiso</strong></p>
                  </div>
                </div>
                <Button onClick={scrollToForm} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-base px-8 py-6" data-testid="cotizar-hero-cta">
                  Cotizar ahora gratis <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="hidden md:block">
                <div className="bg-gradient-to-br from-blue-500/10 to-emerald-500/10 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Ya solicitaron cotización</p>
                      <p className="text-xs text-slate-400">Últimas 24 horas</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: 'Carolina M.', city: 'Santiago', time: 'hace 12 min' },
                      { name: 'Rodrigo P.', city: 'Valparaíso', time: 'hace 45 min' },
                      { name: 'Paulina R.', city: 'Concepción', time: 'hace 1 h' },
                      { name: 'Matías V.', city: 'Temuco', time: 'hace 2 h' }
                    ].map(function(p, i) {
                      return (
                        <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2.5 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-xs font-bold">{p.name[0]}</div>
                            <div>
                              <p className="text-slate-200 text-xs">{p.name}</p>
                              <p className="text-slate-500 text-xs">{p.city}</p>
                            </div>
                          </div>
                          <span className="text-xs text-slate-500">{p.time}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-2">Así de simple</p>
              <h2 className="text-3xl md:text-4xl font-bold">Cómo funciona la cotización</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { n: 1, icon: Send, title: 'Completas el formulario', desc: 'Indica tu región, modalidad buscada y datos básicos. Toma menos de 2 minutos.' },
                { n: 2, icon: MessageCircle, title: 'Recibimos tu solicitud', desc: 'La enviamos por email a los centros y profesionales que coinciden con lo que buscas.' },
                { n: 3, icon: CheckCircle2, title: 'Te contactan directamente', desc: 'Los centros te escriben por WhatsApp o email con sus propuestas personalizadas. Tú eliges.' }
              ].map(function(s) {
                var Icon = s.icon;
                return (
                  <div key={s.n} className="relative bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-blue-500/40 transition">
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">{s.n}</div>
                    <Icon className="w-8 h-8 text-blue-400 mb-4" />
                    <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Elite Centers */}
        {eliteCenters.length > 0 && (
          <section className="py-16 bg-slate-900/40 border-y border-slate-800">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-semibold mb-3">
                  <Crown className="w-3 h-3" /><span>CENTROS ELITE</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Centros destacados en Chile</h2>
                <p className="text-slate-400 max-w-2xl mx-auto">Algunos de los centros Elite que recibirán tu cotización. Equipos verificados, instalaciones de alta calidad y acompañamiento integral.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="elite-centers-grid">
                {eliteCenters.slice(0, 6).map(function(c) {
                  return (
                    <Link key={c.center_id} to={'/centros/' + (c.slug || c.center_id)} className="group" data-testid={'elite-center-' + c.center_id}>
                      <Card className="bg-slate-900/70 border-slate-800 hover:border-emerald-500/50 transition-all h-full">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <h3 className="font-bold text-slate-50 group-hover:text-emerald-400 transition line-clamp-2">{c.name}</h3>
                            <Crown className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          </div>
                          <div className="flex items-center gap-1 text-sm text-slate-400 mb-3">
                            <MapPin className="w-3 h-3" /><span>{c.city}{c.region ? ' · ' + c.region : ''}</span>
                          </div>
                          {c.is_verified && (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-400 mb-3">
                              <BadgeCheck className="w-3 h-3" />Verificado
                            </span>
                          )}
                          <span className="text-sm text-blue-400 inline-flex items-center gap-1 mt-2">Ver perfil <ArrowRight className="w-3 h-3" /></span>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              <div className="text-center mt-8">
                <Button onClick={scrollToForm} size="lg" className="bg-blue-600 hover:bg-blue-700">
                  Cotizar con estos centros <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Form section */}
        <section id="form-section" className="py-16 bg-gradient-to-b from-slate-950 to-slate-900/50">
          <div className="max-w-3xl mx-auto px-4">
            {/* Banner: viene de la experiencia del yo del futuro */}
            {entrySource && entrySource.indexOf('yo-futuro') === 0 && (
              <div
                className="mb-6 p-5 rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-950/60 via-slate-950 to-slate-950 shadow-[0_0_40px_rgba(16,185,129,0.15)]"
                data-testid="landing-banner-yo-futuro"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 border border-emerald-400/30">
                    <Heart className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-300 text-xs uppercase tracking-[0.25em] font-mono mb-1">
                      Viniste desde la Experiencia
                    </p>
                    <p className="text-white text-base sm:text-lg font-medium leading-snug">
                      {form.name ? form.name + ', no' : 'No'} estás solo. Esto es gratis y 100% confidencial.
                    </p>
                    {suggestedModality && (
                      <p className="text-slate-300 text-sm mt-2">
                        Tu yo del futuro te recomendó <strong className="text-emerald-400 capitalize">tratamiento {suggestedModality}</strong>. Lo dejamos pre-seleccionado. Puedes cambiarlo si lo prefieres.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {entrySource === 'experiencia-nelson' && (
              <div
                className="mb-6 p-5 rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-950/60 via-slate-950 to-slate-950"
                data-testid="landing-banner-nelson"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 border border-emerald-400/30">
                    <Heart className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-300 text-xs uppercase tracking-[0.25em] font-mono mb-1">Viniste desde la historia de Nelson</p>
                    <p className="text-white text-base sm:text-lg font-medium leading-snug">
                      No estás solo. Esto es gratis y confidencial.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-xs font-semibold mb-3">
                <Sparkles className="w-3 h-3" /><span>FORMULARIO DE COTIZACIÓN</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Completa tu solicitud</h2>
              <p className="text-slate-400">Tus datos solo van a los centros matching de tu región.</p>
            </div>
            <Card className="bg-slate-900/70 border-slate-800 backdrop-blur">
              <CardContent className="p-6 md:p-8">
                {done ? (
                  done.success ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8" />
                      </div>
                      <h3 className="text-2xl font-bold mb-3" data-testid="landing-success">¡Solicitud enviada!</h3>
                      <p className="text-slate-300 mb-5">
                        Tu mensaje fue enviado a <strong className="text-emerald-400">{done.matched_count}</strong> {form.target_type === 'center' ? 'centros' : 'profesionales'} de {form.region}.
                      </p>
                      <p className="text-slate-400 text-sm mb-6">Te contactarán por WhatsApp o email en las próximas 24 horas. Mientras tanto, revisa tu casilla (también te llegó la lista por email).</p>
                      <Button asChild variant="outline" className="border-slate-700">
                        <Link to="/">Volver al inicio</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-red-400 mb-4">{done.message}</p>
                      <Button onClick={function() { setDone(null); }} className="bg-blue-600 hover:bg-blue-700">Intentar nuevamente</Button>
                    </div>
                  )
                ) : (
                  <>
                    <div className="flex gap-2 mb-6">
                      {[1, 2, 3].map(function(s) {
                        return <div key={s} className={'h-1.5 flex-1 rounded transition ' + (s <= step ? 'bg-blue-500' : 'bg-slate-800')}></div>;
                      })}
                    </div>

                    {step === 1 && (
                      <div className="space-y-5" data-testid="landing-step-1">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-3">¿Qué estás buscando?</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button onClick={function() { update('target_type', 'center'); update('modality', ''); }} data-testid="target-center" className={'p-5 rounded-xl border-2 text-left transition ' + (form.target_type === 'center' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600')}>
                              <Building2 className="w-6 h-6 text-blue-400 mb-2" />
                              <p className="font-semibold text-slate-50">Centro</p>
                              <p className="text-xs text-slate-400 mt-1">Residencial / ambulatorio / online</p>
                            </button>
                            <button onClick={function() { update('target_type', 'professional'); update('modality', ''); }} data-testid="target-pro" className={'p-5 rounded-xl border-2 text-left transition ' + (form.target_type === 'professional' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600')}>
                              <User className="w-6 h-6 text-blue-400 mb-2" />
                              <p className="font-semibold text-slate-50">Profesional</p>
                              <p className="text-xs text-slate-400 mt-1">Psicólogo, psiquiatra, terapeuta</p>
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">¿En qué región?</label>
                          <select value={form.region} onChange={function(e) { update('region', e.target.value); }} data-testid="landing-region" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500">
                            <option value="">Selecciona…</option>
                            {CHILEAN_REGIONS.map(function(r) { return <option key={r.value} value={r.value}>{r.label}</option>; })}
                          </select>
                        </div>
                        <Button onClick={function() { if (canNext1) setStep(2); }} disabled={!canNext1} data-testid="landing-next-1" className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-base">Continuar</Button>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-5" data-testid="landing-step-2">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-3">{form.target_type === 'center' ? 'Modalidad de tratamiento' : 'Tipo de profesional'}</label>
                          <div className="space-y-2">
                            {modalities.map(function(m) {
                              var isSelected = form.modality === m.value;
                              var isWarn = m.warn;
                              return (
                                <button
                                  key={m.value}
                                  onClick={function() { handleModalityClick(m.value); }}
                                  data-testid={'landing-modality-' + m.value}
                                  className={'w-full p-4 rounded-lg border-2 text-left transition ' + (isSelected ? 'border-blue-500 bg-blue-500/10' : isWarn ? 'border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60' : 'border-slate-700 hover:border-slate-600')}
                                >
                                  <p className={'font-semibold ' + (isWarn ? 'text-amber-200' : 'text-slate-50')}>
                                    {m.label} {isWarn && <span className="text-xs font-normal ml-1">⚠️</span>}
                                  </p>
                                  {m.desc && <p className={'text-xs mt-1 ' + (isWarn ? 'text-amber-300/80' : 'text-slate-400')}>{m.desc}</p>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {matchCount !== null && (
                          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-300 flex items-center gap-2" data-testid="match-preview">
                            <Sparkles className="w-4 h-4" /><span><strong>{matchCount}</strong> {form.target_type === 'center' ? 'centros' : 'profesionales'} recibirán tu cotización</span>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de adicción (opcional)</label>
                          <select value={form.addiction} onChange={function(e) { update('addiction', e.target.value); }} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50">
                            <option value="">Selecciona…</option>
                            {ADDICTIONS.map(function(a) { return <option key={a}>{a}</option>; })}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2"><Clock className="w-4 h-4 inline mr-1" />Urgencia</label>
                          <div className="grid grid-cols-3 gap-2">
                            {URGENCY.map(function(u) {
                              return (
                                <button key={u.value} onClick={function() { update('urgency', u.value); }} className={'p-3 rounded-lg border text-sm transition ' + (form.urgency === u.value ? 'border-blue-500 bg-blue-500/10 text-slate-50' : 'border-slate-700 text-slate-300 hover:border-slate-600')}>{u.label}</button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Presupuesto — solo aparece si modality escogida y no es gratis */}
                        {form.modality && form.modality !== 'gratis' && budgetOptions.length > 0 && (
                          <div data-testid="landing-budget-section">
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              <DollarSign className="w-4 h-4 inline mr-1 text-emerald-400" />
                              Presupuesto mensual estimado <span className="text-rose-400">*</span>
                            </label>
                            <div className="space-y-2">
                              {budgetOptions.map(function(b) {
                                var isBudSel = form.budget_range === b.value;
                                return (
                                  <button
                                    key={b.value}
                                    onClick={function() { update('budget_range', b.value); }}
                                    data-testid={'landing-budget-' + b.value}
                                    className={'w-full p-3 rounded-lg border-2 text-left transition ' + (isBudSel ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 hover:border-slate-600')}
                                  >
                                    <p className="font-semibold text-slate-50 text-sm">{b.label}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">{b.hint}</p>
                                  </button>
                                );
                              })}
                              <button
                                onClick={function() { update('budget_range', 'not-specified'); }}
                                data-testid="landing-budget-not-specified"
                                className={'w-full p-3 rounded-lg border-2 border-dashed text-left transition text-sm ' + (form.budget_range === 'not-specified' ? 'border-slate-400 bg-slate-800/60' : 'border-slate-700 hover:border-slate-600 text-slate-400')}
                              >
                                Prefiero no indicar (recibiré propuestas con distintos rangos)
                              </button>
                            </div>
                            <div className="mt-3 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-200/90 flex gap-2">
                              <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                              <span>A mayor precio hay <b>más beneficios de infraestructura, cantidad de atenciones y calidad de profesionales</b>. Consulta el detalle exacto de servicios directamente en cada cotización que recibas.</span>
                            </div>
                            <label className="mt-3 flex items-start gap-2 text-sm text-slate-300 cursor-pointer" data-testid="landing-license-checkbox-wrap">
                              <input
                                type="checkbox"
                                checked={form.can_use_medical_license}
                                onChange={function(e) { update('can_use_medical_license', e.target.checked); }}
                                data-testid="landing-license-checkbox"
                                className="mt-1 w-4 h-4 accent-emerald-500"
                              />
                              <span>Puedo pagar con <b>licencia médica psiquiátrica</b> (ISAPRE/FONASA).</span>
                            </label>
                          </div>
                        )}

                        <div className="flex gap-3">
                          <Button onClick={function() { setStep(1); }} variant="outline" className="flex-1 border-slate-700">Atrás</Button>
                          <Button onClick={function() { if (canNext2) setStep(3); }} disabled={!canNext2} data-testid="landing-next-2" className="flex-1 bg-blue-600 hover:bg-blue-700">Continuar</Button>
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-4" data-testid="landing-step-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Tu nombre</label>
                          <input type="text" value={form.name} onChange={function(e) { update('name', e.target.value); }} placeholder="Nombre completo" data-testid="landing-name" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp / Teléfono</label>
                          <input type="tel" value={form.phone} onChange={function(e) { update('phone', e.target.value); }} placeholder="+56 9 XXXX XXXX" data-testid="landing-phone" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Email (opcional, recomendado)</label>
                          <input type="email" value={form.email} onChange={function(e) { update('email', e.target.value); }} placeholder="tu@email.com" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500" />
                          <p className="text-xs text-slate-500 mt-1">Te enviaremos la lista de centros que recibieron tu cotización.</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Mensaje (opcional)</label>
                          <textarea value={form.message} onChange={function(e) { update('message', e.target.value); }} rows={3} placeholder="Detalles que ayuden a los centros a ofrecerte la mejor propuesta" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 resize-none" />
                        </div>
                        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-xs text-slate-400 flex gap-2">
                          <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span>Tus datos solo irán a los {form.target_type === 'center' ? 'centros' : 'profesionales'} matching de {form.region}. Son 100% confidenciales.</span>
                        </div>
                        <div className="flex gap-3">
                          <Button onClick={function() { setStep(2); }} variant="outline" disabled={loading} className="flex-1 border-slate-700">Atrás</Button>
                          <Button onClick={submit} disabled={!canSubmit || loading} data-testid="landing-submit" className="flex-[2] bg-blue-600 hover:bg-blue-700 py-6">
                            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Enviando…</> : <><Send className="w-4 h-4 mr-2" />Enviar cotización</>}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Guide: What to ask */}
        <section className="py-16 bg-slate-900/30">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-xs font-semibold mb-3">
                <Lightbulb className="w-3 h-3" /><span>GUÍA ÚTIL</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Qué preguntar al cotizar</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">6 preguntas clave para comparar centros y no perder tiempo en lo que realmente importa.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {WHAT_TO_ASK.map(function(w, i) {
                return (
                  <div key={i} className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 hover:border-amber-500/30 transition">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center flex-shrink-0 font-bold text-sm">{i + 1}</div>
                      <div>
                        <h3 className="font-semibold text-slate-50 mb-1.5">{w.title}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{w.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-xs font-semibold mb-3">
                <HelpCircle className="w-3 h-3" /><span>PREGUNTAS FRECUENTES</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Todo lo que necesitas saber</h2>
              <p className="text-slate-400">Resolvemos tus dudas sobre precios, modalidades y el proceso.</p>
            </div>
            <div className="space-y-3" data-testid="faq-list">
              {FAQS.map(function(f, i) {
                var isOpen = openFaq === i;
                return (
                  <div key={i} className={'bg-slate-900/50 border rounded-xl transition ' + (isOpen ? 'border-blue-500/40' : 'border-slate-800')}>
                    <button onClick={function() { setOpenFaq(isOpen ? -1 : i); }} data-testid={'faq-' + i} className="w-full flex items-center justify-between p-5 text-left">
                      <span className="font-semibold text-slate-50 pr-4">{f.q}</span>
                      {isOpen ? <Minus className="w-5 h-5 text-blue-400 flex-shrink-0" /> : <Plus className="w-5 h-5 text-slate-500 flex-shrink-0" />}
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-slate-300 text-sm leading-relaxed border-t border-slate-800/60 pt-4">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Schema.org FAQ markup */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": FAQS.map(function(f) {
                return {
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a }
                };
              })
            }) }} />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-gradient-to-br from-blue-900/30 to-emerald-900/20 border-t border-slate-800">
          <div className="max-w-3xl mx-auto px-4 text-center">
            {/* Social proof: testimonio rotativo del Reto 21 */}
            <Reto21TestimoniosCarousel
              title="Lo que dicen quienes ya empezaron"
              layout="rotator"
              limit={5}
            />
            <h2 className="text-3xl md:text-4xl font-bold mb-4 mt-8">No esperes más para dar el primer paso</h2>
            <p className="text-slate-300 mb-8 leading-relaxed">Cada día que pasa sin tratamiento es un día perdido. Cotizar no te compromete a nada, pero te pone frente a opciones concretas.</p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
              <Button onClick={scrollToForm} size="lg" className="bg-blue-600 hover:bg-blue-700 w-full">
                <Send className="w-4 h-4 mr-2" />Cotizar ahora
              </Button>
              <Button asChild size="lg" variant="outline" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 w-full">
                <a href="tel:1412"><Phone className="w-4 h-4 mr-2" />Fonodrogas 1412</a>
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-6">Servicio gratuito ofrecido por SinAdicciones.org · Directorio nacional</p>
          </div>
        </section>
      </main>

      {/* Modal de advertencia si escoge Gratis */}
      {showGratisModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={function() { setShowGratisModal(false); }}
          data-testid="gratis-modal-backdrop"
        >
          <div
            className="w-full max-w-lg bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden"
            onClick={function(e) { e.stopPropagation(); }}
            data-testid="gratis-modal"
          >
            <div className="relative bg-gradient-to-br from-amber-500/20 to-slate-900 p-5 border-b border-amber-500/30">
              <button
                onClick={function() { setShowGratisModal(false); }}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-100"
                aria-label="Cerrar"
                data-testid="gratis-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-50 leading-tight">Antes de continuar con centros gratuitos</h3>
                  <p className="text-sm text-amber-200/90 mt-1">SENDA / FONASA · Programa público</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3.5">
                <div className="font-semibold text-rose-200 mb-1 flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> Listas de espera reales: 1 a 5 meses
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Los cupos gratuitos son limitados. En crisis o urgencias, esperar meses puede <b>no ser recomendable</b>: es cuando más aumenta el riesgo de recaída y deterioro.
                </p>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3.5">
                <div className="font-semibold text-emerald-200 mb-1 flex items-center gap-1.5">
                  <BadgeCheck className="w-4 h-4" /> Alternativa: licencia médica psiquiátrica
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Un psiquiatra puede emitir <b>licencia médica</b> que cubre parte del tratamiento privado a través de tu ISAPRE o FONASA. Muchos centros privados aceptan este pago.
                </p>
              </div>
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3.5">
                <div className="font-semibold text-blue-200 mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" /> Cotizar centros privados es <b>gratuito y sin compromiso</b>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Recibe hasta 20 propuestas con precios reales y disponibilidad inmediata. Muchos centros ofrecen planes desde $350.000 con opción a licencia médica.
                </p>
              </div>
            </div>
            <div className="p-5 pt-0 space-y-2">
              <Button
                onClick={function() {
                  setShowGratisModal(false);
                  // Reset modality choice para forzar re-selección con presupuesto
                  update('modality', '');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 font-semibold"
                data-testid="gratis-modal-cotizar-privado"
              >
                Prefiero cotizar centros privados
              </Button>
              <button
                onClick={function() {
                  setShowGratisModal(false);
                  // Registrar intención GA4
                  try {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'cotizador_gratis_redirect', { event_category: 'cotizacion', event_label: form.region || 'no_region' });
                    }
                  } catch (_gtagErr) { /* noop */ }
                  navigate('/centros-de-rehabilitacion-de-drogas-gratis');
                }}
                className="w-full py-2.5 text-xs text-slate-400 hover:text-slate-200 underline"
                data-testid="gratis-modal-continue"
              >
                Igual quiero ver la guía de centros gratuitos →
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
